/**
 * TeamRfpRecord — "What was sent" under an RFP round in Reference > Team (TeamSection.jsx's
 * RfpRow, and the rfp-detail: zoom). Matt, 2026-10-27: "I want everything the RFP sent to be
 * recorded in the Cortex Outline under the team branch so we can keep track of it."
 *
 * Reads only the frozen columns on the `consultant_rfps` row and its recipients (database/360,
 * 361) — never re-derives anything, so it shows what actually went out even after the project's
 * facts, scope or drawings change. Rows sent before 2026-10-27 have only recipients, note and the
 * request PDF; each branch renders only when its data exists.
 *
 * Plain outline rows: collapsible branches (wf.collapsed, collapsed by default, data-key on every
 * node) and leaves; a leaf with a file opens it in a new tab. Nothing here is zoomable on its own —
 * the RFP's own rfp-detail: zoom is the zoom for the whole record.
 */

import { wf, saveViewState } from '../outline/state.js'
import { rfpFactLines } from '../../../api/_rfpFacts.js'

const MUTED = '#7a9aaa'

function toggleKey(key, collapsed) {
  return (e) => { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
}

function RecBranch({ k, label, meta, children }) {
  const collapsed = wf.collapsed[k] !== false
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={k}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleKey(k, collapsed)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {meta && <span className="wf-meta" style={{ marginLeft: 8, color: MUTED, fontSize: 11 }}>{meta}</span>}
        </div>
      </div>
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

function RecLeaf({ k, text, meta, href, color, mono }) {
  return (
    <div className="wf-node wf-node--item" data-key={k}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          {href
            ? <a className="wf-node-text" href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--aj-accent)', textDecoration: 'none' }}>{text}</a>
            : <span className="wf-node-text" style={{ ...(color ? { color } : {}), whiteSpace: 'pre-wrap' }}>
                {mono && <span style={{ fontFamily: '"DM Mono",monospace', marginRight: 6 }}>{mono}</span>}{text}
              </span>}
          {meta && <span className="wf-meta" style={{ marginLeft: 8, color: MUTED, fontSize: 11 }}>{meta}</span>}
        </div>
      </div>
    </div>
  )
}

const STATUS = {
  sent: { text: 'Emailed', color: '#2e7d4f' },
  failed: { text: 'Not delivered', color: '#C0392B' },
  no_email: { text: 'No email address, not sent', color: '#C0392B' },
  not_sent: { text: 'Not sent, email is not set up', color: '#C0392B' },
}

const DELIVERY = { attached: 'attached to the email', linked: 'too large to attach, sent as a download link' }

/** The record's rows — rendered inside a parent's wf-children (tree) or a zoom's wf-list. */
export function RfpRecordRows({ rfp }) {
  const base = 'sec:team:rfp:' + rfp.id + ':sent'
  const recipients = rfp.recipients || []
  const drawings = Array.isArray(rfp.drawings) ? rfp.drawings : null
  const inSet = drawings ? drawings.filter(d => d.included) : []
  const groups = (rfp.scope_of_work && rfp.scope_of_work.groups) || []
  const facts = rfp.facts ? rfpFactLines(rfp.consultant_service, rfp.facts, rfp.scope_of_work) : []
  const services = String(rfp.services || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean)
  const sentBy = rfp.sent_at ? new Date(rfp.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''

  return (
    <>
      <RecBranch k={base + ':to'} label="Sent to" meta={recipients.length + ' · ' + sentBy}>
        {recipients.length === 0 && <RecLeaf k={base + ':to:none'} text="No recipients recorded" color={MUTED} />}
        {recipients.map(r => {
          const st = STATUS[r.email_status]
          return (
            <RecLeaf key={r.id} k={base + ':to:' + r.id} text={(r.contact_name || '') + (r.email ? ' <' + r.email + '>' : '')}
              meta={st ? st.text + (r.email_error ? ': ' + r.email_error : '') : null} />
          )
        })}
      </RecBranch>

      {rfp.email_subject && (
        <RecBranch k={base + ':email'} label="Email" meta={rfp.email_subject}>
          {String(rfp.email_text || '').split(/\n\n+/).map((p, i) => <RecLeaf key={i} k={base + ':email:' + i} text={p} />)}
        </RecBranch>
      )}

      {rfp.pdf_url
        ? <RecLeaf k={base + ':pdf'} text="Request for proposal (PDF)" href={rfp.pdf_url} />
        : <RecLeaf k={base + ':pdf'} text="Request PDF not saved" color="#C0392B" />}

      {drawings && (
        <RecBranch k={base + ':drawings'} label="Drawings"
          meta={drawings.length === 0 ? 'none posted, nothing sent'
            : inSet.length + ' sheet' + (inSet.length === 1 ? '' : 's') + (DELIVERY[rfp.drawings_delivery] ? ', ' + DELIVERY[rfp.drawings_delivery] : ', not sent')}>
          {rfp.drawings_pdf_url && <RecLeaf k={base + ':drawings:pdf'} text="Drawing set as sent (PDF)" href={rfp.drawings_pdf_url} />}
          {drawings.map((d, i) => (
            <RecLeaf key={i} k={base + ':drawings:' + i} mono={d.number} text={d.title}
              color={d.included ? undefined : MUTED}
              meta={d.included ? [d.milestone, d.postedDate].filter(Boolean).join(', ') : 'left out, no PDF'} />
          ))}
        </RecBranch>
      )}

      {groups.length > 0 && (
        <RecBranch k={base + ':scope'} label="Scope of work">
          {groups.map((g, gi) => (
            <RecBranch key={gi} k={base + ':scope:' + gi} label={g.title}>
              {g.lines.map((l, li) => <RecLeaf key={li} k={base + ':scope:' + gi + ':' + li} text={l} />)}
            </RecBranch>
          ))}
        </RecBranch>
      )}

      {facts.length > 0 && (
        <RecBranch k={base + ':facts'} label="Project facts" meta={(() => {
          const open = facts.filter(f => f.answer === 'To be confirmed').length
          return (facts.length - open) + ' answered' + (open ? ', ' + open + ' to be confirmed' : '')
        })()}>
          {facts.map((f, i) => <RecLeaf key={i} k={base + ':facts:' + i} text={f.label} meta={f.answer} />)}
        </RecBranch>
      )}

      {services.length > 0 && (
        <RecBranch k={base + ':services'} label="Services requested" meta={String(services.length)}>
          {services.map((s, i) => <RecLeaf key={i} k={base + ':services:' + i} text={s} />)}
        </RecBranch>
      )}

      {rfp.notes && <RecLeaf k={base + ':note'} text={'Note: ' + rfp.notes} />}
    </>
  )
}

/** The collapsible "What was sent" branch under an RfpRow in the tree. */
export default function TeamRfpRecord({ rfp }) {
  return (
    <RecBranch k={'sec:team:rfp:' + rfp.id + ':sent'} label="What was sent">
      <RfpRecordRows rfp={rfp} />
    </RecBranch>
  )
}
